/**
 * useCitationRevocationActions — Bundle 1 C2 action-wiring hook.
 *
 * Returns React-Query mutation handles for the chip's two action paths
 * (acknowledge tombstone / escalate to owner) and an aggregate `onAction`
 * callback shaped to plug straight into <CitationRevocationChip>'s
 * `onAction` prop. On success, invalidates the by-artifact citation query
 * key used by `useRevocationViewMap` so the chip transitions to its new
 * ack_status visually (`pending → acknowledged → escalated`) via a real
 * cache refresh — NOT via local component state, which would diverge from
 * the backend on multi-tab / multi-user transitions.
 *
 * The acting identity is NOT sent by the client — the backend derives it
 * server-side from the verified session cookie (`current_actor`, post-T-FS-1;
 * the acknowledge/escalate endpoints take no body). The hook still accepts a
 * `getActor: () => string | null` callback purely as a CLIENT-SIDE GATE: when
 * `getActor()` returns `null` (no local principal known yet), the mutation is a
 * deliberate no-op — the chip click does not fire a POST. Tests can assert a
 * no-op by mocking `getActor` to return null.
 */
import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  type CitationRevocationApiOptions,
  type CitationBackendResponse,
} from './api'
import {
  postAcknowledgeCitation,
  postEscalateCitation,
} from './actions'

export interface UseCitationRevocationActionsOptions
  extends CitationRevocationApiOptions {
  /**
   * Lazily resolve the actor at click time. Return `null` to indicate "no
   * authenticated principal known yet" — the action will be a deliberate
   * no-op (no POST fires). The route typically wires this to a `useMe`-style
   * principal lookup, e.g. `() => me?.principal_id ?? null`.
   */
  getActor: () => string | null
}

/**
 * Intent payload accepted by `onAction` — matches the chip's
 * `CitationRevocationChipProps.onAction` argument (this wave extends the
 * intent to include `artifact_id` so the handler can invalidate the right
 * query key without an extra lookup).
 */
export interface CitationRevocationActionIntent {
  citation_id: string
  artifact_id: string
  action: 'acknowledge' | 'escalate'
  endpoint: string
}

export interface UseCitationRevocationActionsResult {
  /** Imperative API for direct callers (route-level tests, etc.). */
  acknowledge: (citationId: string, artifactId: string) => Promise<CitationBackendResponse | null>
  escalate: (citationId: string, artifactId: string) => Promise<CitationBackendResponse | null>
  /**
   * Per-citation in-flight resolver. Returns true iff an acknowledge OR
   * escalate POST for this citation_id is currently in flight. Multiple
   * citations fire concurrently without blocking each other — the resolver
   * is keyed by citation_id, NOT by global mutation state. (Bundle 1 C2
   * action-error-UX wave: chips disable their action button while pending.)
   */
  isPendingFor: (citationId: string) => boolean
  /**
   * Per-citation last-error resolver. Returns the most recent action Error
   * for this citation_id, or null if no error is currently surfaced. Cleared
   * automatically when the next mutation for the same citation_id starts
   * (`onMutate`), succeeds (`onSuccess`), or via `clearError`.
   */
  errorFor: (citationId: string) => Error | null
  /**
   * Explicit error reset for a citation. Useful when the caller wants to
   * dismiss the inline alert without firing a new action (e.g. close button
   * on the alert). Not used by the default retry path — retry just clicks
   * the action button again, which auto-clears via onMutate.
   */
  clearError: (citationId: string) => void
  /**
   * Per-citation optimistic-action resolver (Plan 32 Option A — Bundle 1 C2
   * optimistic-UX wave). Returns the specific action currently in flight for
   * this citation_id (`'acknowledge'` or `'escalate'`), or null when nothing
   * is pending for this citation. Lifecycle is identical to `isPendingFor`:
   * set in `onMutate`, cleared in `onSettled`. Honest semantics — this is a
   * "user clicked, POST in flight" hint only; it does NOT claim the server
   * has confirmed the ack/escalation. On success the real `ack_status`
   * flows in via the by-artifact refetch and supersedes this resolver
   * (which has cleared on settled by then).
   */
  optimisticActionFor: (citationId: string) => 'acknowledge' | 'escalate' | null
  /**
   * Chip-shaped callback. Plugs straight into
   * `<CitationRevocationChip onAction={...} />` (after the chip's intent is
   * extended to include `artifact_id` in this same wave). Fire-and-forget;
   * errors are tracked on `error`.
   */
  onAction: (intent: CitationRevocationActionIntent) => void
  isPending: boolean
  error: Error | null
}

/**
 * Build the action handlers + chip-shaped callback. Safe to call from a
 * route component that uses `useRevocationViewMap` — the mutation
 * invalidates the same by-artifact citation key the map's pass-2 useQueries
 * reads, so the chip refreshes through the standard refetch path with no
 * local state.
 */
export function useCitationRevocationActions(
  opts: UseCitationRevocationActionsOptions,
): UseCitationRevocationActionsResult {
  const queryClient = useQueryClient()

  // Per-citation pending + error state for the chip action-error-UX wave.
  // Keyed by citation_id so multiple chips firing concurrently don't block
  // or cross-pollute each other's state. Uses Set/Map updates via
  // immutably-cloned containers so React reference-equality triggers
  // re-render (a mutation in-place would not).
  const [pendingCitations, setPendingCitations] = useState<Set<string>>(
    () => new Set(),
  )
  const [errorsByCitation, setErrorsByCitation] = useState<Map<string, Error>>(
    () => new Map(),
  )
  // Plan 32 Option A — Bundle 1 C2 optimistic-UX wave: per-citation in-flight
  // action key (acknowledge / escalate), tracked separately from `pendingCitations`
  // so the chip can swap its action button label for an action-specific transient
  // status ("Acknowledging…" / "Contacting owner…") while POST is in flight. This
  // is a UI hint only — the real ack_status arrives via the by-artifact refetch
  // after success and supersedes this map (which clears on settled regardless).
  const [pendingActionsByCitation, setPendingActionsByCitation] = useState<
    Map<string, 'acknowledge' | 'escalate'>
  >(() => new Map())

  // Pure fetch + react-query mutation wrapper. The mutation does NOT take
  // the actor as part of the variables — actor is resolved lazily by the
  // hook on each call so the route doesn't have to re-render to pick up
  // a login state change.
  const mutation = useMutation<
    CitationBackendResponse,
    Error,
    { citationId: string; artifactId: string; action: 'acknowledge' | 'escalate' }
  >({
    mutationFn: async (vars) => {
      if (vars.action === 'acknowledge') {
        return postAcknowledgeCitation(vars.citationId, opts)
      }
      return postEscalateCitation(vars.citationId, opts)
    },
    onMutate: (vars) => {
      // Action starts — mark this citation pending and clear any prior
      // error so the chip's inline alert dismisses on retry-click.
      setPendingCitations((prev) => {
        const next = new Set(prev)
        next.add(vars.citationId)
        return next
      })
      setErrorsByCitation((prev) => {
        if (!prev.has(vars.citationId)) return prev
        const next = new Map(prev)
        next.delete(vars.citationId)
        return next
      })
      // Plan 32 Option A — record the specific action for the optimistic UI
      // hint. Cleared on onSettled regardless of success/failure.
      setPendingActionsByCitation((prev) => {
        const next = new Map(prev)
        next.set(vars.citationId, vars.action)
        return next
      })
    },
    onError: (err, vars) => {
      setErrorsByCitation((prev) => {
        const next = new Map(prev)
        next.set(vars.citationId, err)
        return next
      })
    },
    onSuccess: (_data, vars) => {
      // Refresh the by-artifact citation query that `useRevocationViewMap`
      // pass-2 reads. Query key shape must match exactly:
      //   ['citation-revocation', 'citations-by-artifact', apiBase, artifactId]
      // (see useRevocationViewMap.ts pass-2 useQueries config).
      queryClient.invalidateQueries({
        queryKey: [
          'citation-revocation',
          'citations-by-artifact',
          opts.apiBase,
          vars.artifactId,
        ],
      })
    },
    onSettled: (_data, _err, vars) => {
      // Always clears pending — success and failure both leave the citation
      // not-in-flight. Error state (if set) survives until next mutation or
      // explicit clearError.
      setPendingCitations((prev) => {
        if (!prev.has(vars.citationId)) return prev
        const next = new Set(prev)
        next.delete(vars.citationId)
        return next
      })
      // Plan 32 Option A — clear the optimistic-action hint on settled
      // (regardless of success/failure). On success, the real ack_status
      // arrives via the invalidated by-artifact query; on failure, the
      // chip falls back to its prior state + renders the inline error.
      setPendingActionsByCitation((prev) => {
        if (!prev.has(vars.citationId)) return prev
        const next = new Map(prev)
        next.delete(vars.citationId)
        return next
      })
    },
  })

  const isPendingFor = useCallback(
    (citationId: string): boolean => pendingCitations.has(citationId),
    [pendingCitations],
  )

  const errorFor = useCallback(
    (citationId: string): Error | null =>
      errorsByCitation.get(citationId) ?? null,
    [errorsByCitation],
  )

  const clearError = useCallback((citationId: string): void => {
    setErrorsByCitation((prev) => {
      if (!prev.has(citationId)) return prev
      const next = new Map(prev)
      next.delete(citationId)
      return next
    })
  }, [])

  const optimisticActionFor = useCallback(
    (citationId: string): 'acknowledge' | 'escalate' | null =>
      pendingActionsByCitation.get(citationId) ?? null,
    [pendingActionsByCitation],
  )

  async function _runAction(
    citationId: string,
    artifactId: string,
    action: 'acknowledge' | 'escalate',
  ): Promise<CitationBackendResponse | null> {
    if (citationId === '') return null
    // Keep the client-side gate: when no local principal is known yet, no-op so
    // the chip click doesn't fire a doomed request. The acting identity itself is
    // the verified session cookie (server-side current_actor) — never sent in the body.
    if (opts.getActor() === null) return null
    return mutation.mutateAsync({ citationId, artifactId, action })
  }

  const acknowledge = (citationId: string, artifactId: string): Promise<CitationBackendResponse | null> =>
    _runAction(citationId, artifactId, 'acknowledge')

  const escalate = (citationId: string, artifactId: string): Promise<CitationBackendResponse | null> =>
    _runAction(citationId, artifactId, 'escalate')

  const onAction = (intent: CitationRevocationActionIntent): void => {
    // Fire-and-forget; mutation.error captures any failure. The chip stays
    // in its current state until the by-artifact refetch lands; on success
    // the cache invalidation triggers the new ack_status to flow through
    // useRevocationViewMap → ChunkRow → chip naturally.
    void _runAction(intent.citation_id, intent.artifact_id, intent.action)
  }

  return {
    acknowledge,
    escalate,
    onAction,
    isPending: mutation.isPending,
    error: mutation.error ?? null,
    isPendingFor,
    errorFor,
    clearError,
    optimisticActionFor,
  }
}
